import { Avatar, AvatarFallback, AvatarImage } from '@/app/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/app/ui/tooltip';
import { cn } from '@/helpers/shadcn';

// Reusable circular avatar for a user. Renders the user's `avatar_url` when present
// and falls back to their initials (first letter of firstname + lastname). Purely
// presentational — callers pass a user object from whatever source they already have
// (/self, the company users list, a denormalized author, etc.). Sources that don't
// carry `avatar_url` simply render the initials fallback; images light up for free
// once that field is included.
//
// A subtle ring is baked in so the circle keeps a visible edge against any
// background (photos and light initials can otherwise blend into the surface).
//
// `tooltip` wraps the avatar in a hover tooltip showing the full name — on by
// default so a bare face/initials always identifies its owner. Pass `tooltip={false}`
// where a name already sits beside the avatar or it would nest inside another tooltip.
// `className`/`fallbackClassName` size and style the circle; `imageTestId`/
// `fallbackTestId` attach data-testids where a caller's tests need them.
const ViewUserAvatar = ({ user, className, fallbackClassName, imageTestId, fallbackTestId, tooltip = true }) => {
  const firstname = user?.firstname ?? '';
  const lastname = user?.lastname ?? '';
  const initials = `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase();
  const name = `${firstname} ${lastname}`.trim() || user?.name || '';

  const avatar = (
    <Avatar className={cn('ring-1 ring-foreground/10', className)}>
      <AvatarImage src={user?.avatar_url || ''} alt={name} data-testid={imageTestId} />
      <AvatarFallback className={fallbackClassName} data-testid={fallbackTestId}>
        {initials}
      </AvatarFallback>
    </Avatar>
  );

  if (!tooltip || !name) {
    return avatar;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{avatar}</TooltipTrigger>
      <TooltipContent>
        <p>{name}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export default ViewUserAvatar;
